import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, ArrowLeft } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];

// "2026-10-16T10:30:00+00:00" -> "16 October 2026" (UTC, same as the PDF)
function formatDateLong(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso.slice(0, 10);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

function formatHours(h) {
  const v = Number(h);
  if (!v || isNaN(v) || v <= 0) return "";
  return `${v} ${v === 1 ? "Hour" : "Hours"}`;
}

// Mirrors generate_certificate_pdf in server.py (A4 landscape points, 1491x1055 template px).
const PAGE_W = 841.89;
const PAGE_H = 595.28;
const SX = PAGE_W / 1491;
const SY = PAGE_H / 1055;
const CENTER_X = 745.5 * SX;
const NAME_Y = 494 * SY;
const TITLE_Y = 638 * SY;
const DETAILS_Y = 850 * SY;
const DETAIL_XS = [434.5 * SX, 745.5 * SX, 1061 * SX];

function fitSize(text, family, start, maxWidth, min) {
  const ctx = document.createElement("canvas").getContext("2d");
  let size = start;
  ctx.font = `${size}px "${family}"`;
  while (size > min && ctx.measureText(text).width > maxWidth) {
    size -= 1;
    ctx.font = `${size}px "${family}"`;
  }
  return size;
}

function useCertFonts() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    Promise.all([
      document.fonts.load('40px "Cert Name"'),
      document.fonts.load('24px "Cert Title"'),
      document.fonts.load('14px "Cert Body"'),
    ]).finally(() => setReady(true));
  }, []);
  return ready;
}

export default function CertificatePage() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [err, setErr] = useState(null);
  const fontsReady = useCertFonts();

  useEffect(() => {
    api.get(`/candidate/session?token=${token}`)
      .then((r) => setSession(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Invalid link"));
  }, [token]);

  if (err) return <div className="min-h-screen flex items-center justify-center font-mono text-sm text-destructive">{err}</div>;
  if (!session) return <div className="min-h-screen flex items-center justify-center font-mono text-sm text-muted-foreground">Loading…</div>;

  const { candidate, course, progress } = session;
  const completed = progress?.status === "completed";

  if (!completed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-black tracking-tighter">Certificate locked</h1>
          <p className="mt-3 text-sm text-muted-foreground">Complete the course (all modules + pass the quiz) to unlock your certificate.</p>
          <Link to={`/learn/${token}`} className="mt-6 inline-flex items-center gap-2 text-sm underline">
            <ArrowLeft size={14} /> Back to course
          </Link>
        </div>
      </div>
    );
  }

  const downloadUrl = `${API_BASE}/candidate/certificate?token=${token}`;
  const templateUrl = `${API_BASE}/candidate/certificate-template?token=${token}`;
  const details = [
    formatDateLong(progress?.completed_at),
    formatHours(course?.cpd_hours),
    progress?.certificate_number || "",
  ];
  const detailIds = ["certificate-date", "certificate-cpd-hours", "certificate-number"];
  const fit = (text, family, start, max, min) => (fontsReady ? fitSize(text, family, start, max, min) : start);
  const nameSize = fit(candidate.name, "Cert Name", 40, PAGE_W * 0.6, 10);
  const titleSize = fit(course.title, "Cert Title", 24, PAGE_W * 0.54, 8);

  return (
    <div className="min-h-screen bg-secondary">
      <header className="no-print border-b border-border bg-background">
        <div className="max-w-5xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to={`/learn/${token}`} className="font-mono text-xs tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1">
            <ArrowLeft size={12} /> BACK TO COURSE
          </Link>
          <div className="flex items-center gap-3">
            <Button onClick={() => window.print()} variant="outline" data-testid="print-certificate-button" className="rounded-sm h-10">
              Print
            </Button>
            <a href={downloadUrl} download>
              <Button data-testid="download-certificate-button" className="rounded-sm h-10 bg-foreground text-background hover:bg-foreground/90">
                <Download size={14} strokeWidth={1.5} className="mr-2" /> Download PDF
              </Button>
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-6 md:p-12">
        <div
          className="printable-certificate relative w-full shadow-lg overflow-hidden bg-white"
          style={{ aspectRatio: `${PAGE_W} / ${PAGE_H}` }}
          data-testid="certificate-preview"
        >
          <img
            src={templateUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-fill select-none pointer-events-none"
            draggable={false}
          />

          <svg
            viewBox={`0 0 ${PAGE_W} ${PAGE_H}`}
            preserveAspectRatio="none"
            className="absolute inset-0 w-full h-full"
            style={{ visibility: fontsReady ? "visible" : "hidden" }}
          >
            <text x={CENTER_X} y={NAME_Y} textAnchor="middle" fill="#0A2E78"
              fontFamily='"Cert Name"' fontSize={nameSize} data-testid="certificate-candidate-name">
              {candidate.name}
            </text>
            <text x={CENTER_X} y={TITLE_Y} textAnchor="middle" fill="#FFFFFF"
              fontFamily='"Cert Title"' fontSize={titleSize} data-testid="certificate-course-title">
              {course.title}
            </text>
            {details.map((text, i) => (
              <text key={detailIds[i]} x={DETAIL_XS[i]} y={DETAILS_Y} textAnchor="middle" fill="#0A1F5C"
                fontFamily='"Cert Body"' fontSize={fit(text, "Cert Body", 14, PAGE_W * 0.16, 9)}
                data-testid={detailIds[i]}>
                {text}
              </text>
            ))}
          </svg>
        </div>
      </main>
    </div>
  );
}
